import { Button, Icon, Modal } from "@legal-500/legal500-design-system";
import styles from "./Modal.module.css";

export type BulkParsePhase = "confirm" | "progress" | "done" | "cancelled" | "error";

type BulkParseProgressModalProps = {
  phase: BulkParsePhase;
  total: number;
  processed: number;
  enqueued: number;
  skipped: number;
  errorMessage?: string | null;
  /** True once a cancel has been requested but the in-flight chunk is still running. */
  cancelRequested: boolean;
  onConfirm: () => void;
  onCancelRun: () => void;
  onClose: () => void;
};

export function BulkParseProgressModal({
  phase,
  total,
  processed,
  enqueued,
  skipped,
  errorMessage,
  cancelRequested,
  onConfirm,
  onCancelRun,
  onClose,
}: BulkParseProgressModalProps) {
  const isRunning = phase === "progress";
  const percent = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
  const skippedSuffix = skipped > 0 ? `, skipped ${skipped}` : "";

  // While batches are running we swallow close requests (backdrop + the X
  // button) so in-progress work is never lost; the explicit Close button in the
  // terminal phases drives termination instead.
  const noopClose = (): void => {
    // intentionally empty
  };

  return (
    <Modal
      isOpen={true}
      onClose={isRunning ? noopClose : onClose}
      title="Parse all unparsed files"
      size="small"
      blurBackground
      backdropClick={false}
    >
      <div className={styles.bulkParse}>
        {phase === "confirm" && (
          <>
            <p className={styles.bulkParseMessage}>
              Found {total} unparsed file(s) matching the current filters. Parse them?
            </p>
            <div className={styles.modalFooter}>
              <Button onClick={onClose} variant="outlined" color="default" aria-label="Cancel">
                Cancel
              </Button>
              <Button onClick={onConfirm} variant="contained" color="default" aria-label="Confirm">
                Confirm
              </Button>
            </div>
          </>
        )}

        {phase === "progress" && (
          <>
            <div className={styles.bulkParseProgressTrack}>
              <div className={styles.bulkParseProgressBar} style={{ width: `${percent}%` }} />
            </div>
            <p className={styles.bulkParseProgressText}>
              Processing {processed} / {total}…
            </p>
            <div className={styles.modalFooter}>
              <Button
                onClick={onCancelRun}
                variant="outlined"
                color="default"
                disabled={cancelRequested}
                aria-label="Cancel"
              >
                {cancelRequested ? "Cancelling…" : "Cancel"}
              </Button>
            </div>
          </>
        )}

        {phase === "done" && (
          <>
            <div className={styles.bulkParseSummary}>
              <Icon icon="check" size="small" className={styles.successIcon} />
              <span>
                Queued {enqueued} file(s){skippedSuffix}
              </span>
            </div>
            <div className={styles.modalFooter}>
              <Button onClick={onClose} variant="contained" color="default" aria-label="Close">
                Close
              </Button>
            </div>
          </>
        )}

        {phase === "cancelled" && (
          <>
            <p className={styles.bulkParseMessage}>Cancelled — queued {enqueued} so far.</p>
            <div className={styles.modalFooter}>
              <Button onClick={onClose} variant="contained" color="default" aria-label="Close">
                Close
              </Button>
            </div>
          </>
        )}

        {phase === "error" && (
          <>
            <div className={styles.formError}>
              <Icon icon="exclamation" size="small" />
              <span>{errorMessage}</span>
            </div>
            <p className={styles.bulkParseMessage}>
              Queued {enqueued} file(s){skippedSuffix} before the error.
            </p>
            <div className={styles.modalFooter}>
              <Button onClick={onClose} variant="contained" color="default" aria-label="Close">
                Close
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
